import { Button, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { THUMBNAIL_OVERLAY_BTN_CLASS } from '../TemplatesMenu.config';

/**
 * THUMBNAIL OVERLAY COMPONENT
 *
 * Renders hover overlay for template thumbnails.
 * Renders 'Add Page', 'View All Pages', or 'Upgrade' button based on props
 */
export interface OverlayProps {
  onClick: () => void;
  show: boolean;
  addPageIsDisabled: boolean;
  isMultipage: boolean;
  isLoading: boolean;

  // TRUE if user has permission to add template
  // If FALSE, `Upgrade` button will be displayed
  userHasPermission: boolean;
}

export const ThumbnailOverlay = ({
  onClick,
  show,
  userHasPermission,
  addPageIsDisabled,
  isLoading,
  isMultipage,
}: OverlayProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.templatesMenu' });

  let buttonVariant: string, buttonText: string;
  const buttonIsDisabled = userHasPermission && addPageIsDisabled && !isMultipage;

  const handleClick = () => {
    if (buttonIsDisabled) return;
    onClick();
  };

  if (userHasPermission) {
    buttonVariant = 'action';
    buttonText = isMultipage ? t('preview.viewPages') : t('preview.addPage');
  } else {
    buttonVariant = 'upgrade';
    buttonText = t('preview.upgrade');
  }

  return (
    <Flex
      bg='blackAlpha.300'
      position='absolute'
      top={0}
      left={0}
      p={'inherit'}
      w='full'
      h='full'
      borderRadius='base'
      justifyContent='center'
      alignItems='center'
      opacity={show ? '1' : '0'}
      transitionDuration={'200ms'}
      data-testid='template-thumbnail-overlay'
      onClick={handleClick}
      cursor={buttonIsDisabled ? 'not-allowed' : 'pointer'}
    >
      <Button
        variant={buttonVariant}
        data-testid='template-overlay-btn'
        isDisabled={buttonIsDisabled}
        isLoading={isLoading}
        loadingText={t('preview.loadingText')}
        className={THUMBNAIL_OVERLAY_BTN_CLASS}
      >
        {buttonText}
      </Button>
    </Flex>
  );
};
